import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {
  DragAndDropService,
  EventRenderedArgs,
  EventSettingsModel,
  PopupOpenEventArgs,
  ScheduleComponent,
  TimeScaleModel,
  WeekService
} from '@syncfusion/ej2-angular-schedule';
import {
  ISchedule,
  IScheduleOverride,
  IScheduleSlot
} from '@appgen-health-crm/api-interfaces';
import { addDays, addMinutes, isAfter, isWithinInterval } from 'date-fns';
import { ORG_TIMEZONE } from '@appgen-health-crm/data';
import spacetime from 'spacetime';
import { combineDateAndTime } from '@care-portals/shared';

@Component({
  selector: 'app-schedule-calendar',
  templateUrl: './schedule-calendar.component.html',
  styleUrls: ['./schedule-calendar.component.less'],
  providers: [WeekService, DragAndDropService]
})
export class ScheduleCalendarComponent implements OnInit, OnChanges {
  constructor() {}

  @ViewChild('scheduleObj')
  public scheduleObj: ScheduleComponent;

  @Input() schedule: ISchedule;
  @Input() version: number;
  @Output() slotAdded = new EventEmitter<IScheduleSlot>();
  @Output() slotUpdated = new EventEmitter<{
    slot: IScheduleSlot;
    updates: Partial<IScheduleSlot>;
  }>();
  @Output() slotDeleted = new EventEmitter<IScheduleSlot>();
  @Output() overrideAdded = new EventEmitter<IScheduleOverride>();
  @Output() overrideUpdated = new EventEmitter<{
    override: IScheduleOverride;
    updates: Partial<IScheduleOverride>;
  }>();
  @Output() overrideDeleted = new EventEmitter<IScheduleOverride>();
  showStartTimeAfterEndTimeError = false;
  showAvailabilityOverlapError = false;
  eventSettings: EventSettingsModel = {
    dataSource: [],
    allowEditing: false,
    enableTooltip: true
  };
  isVisible = false;
  currentView = 'Week';
  activeSlot: {
    id?: number;
    startTime: Date;
    endTime: Date;
    date?: Date;
    isAvailable?: boolean;
    edit?: boolean;
    data: any;
  } = null;
  radioValue = 'AvailabilitySlot';
  selectedDate = new Date();
  timeScale: TimeScaleModel = { enable: true, interval: 60, slotCount: 1 };
  dateFormat = 'yyyy/MM/dd';

  availability;
  overrides;

  disabledDate = (date: Date): boolean => {
    if (date > new Date()) {
      return false;
    }
    return true;
  };

  ngOnInit() {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.schedule.currentValue) {
      this.renderEventsFromSchedule();
    }
  }

  popupOpenHandler(e: PopupOpenEventArgs) {
    e.cancel = true;

    if (!e.data.Id) {
      this.radioValue = 'AvailabilitySlot';
    } else if (e.data.date) {
      this.radioValue = 'Override';
    } else {
      this.radioValue = 'AvailabilitySlot';
    }

    this.activeSlot = {
      // for two way binding
      edit: !!e.data.Id,
      date: new Date(e.data.date || e.data.StartTime),
      startTime: new Date(e.data.StartTime),
      endTime: new Date(e.data.EndTime),
      // has all the original slot data
      data: e.data
    };
    this.isVisible = true;
  }

  handleOk(): void {
    this.activeSlot.startTime = combineDateAndTime(
      this.activeSlot.date,
      this.activeSlot.startTime
    );
    this.activeSlot.endTime = combineDateAndTime(
      this.activeSlot.date,
      this.activeSlot.endTime
    );

    if (isAfter(this.activeSlot.startTime, this.activeSlot.endTime)) {
      this.showStartTimeAfterEndTimeError = true;
      return;
    }

    // check for overlap
    if (
      this.radioValue === 'AvailabilitySlot' &&
      this.checkAvailabilityOverlap()
    ) {
      this.showAvailabilityOverlapError = true;
      return;
    }

    if (this.radioValue === 'AvailabilitySlot') {
      const startInOrgTimeZone = spacetime(this.activeSlot.startTime).goto(
        this.schedule?.timezone || ORG_TIMEZONE
      );
      const endInOrgTimeZone = spacetime(this.activeSlot.endTime).goto(
        this.schedule?.timezone || ORG_TIMEZONE
      );
      const startMinute =
        startInOrgTimeZone.hour() * 60 + startInOrgTimeZone.minute();
      const endMinute =
        endInOrgTimeZone.hour() * 60 + endInOrgTimeZone.minute();
      const weekDay = startInOrgTimeZone.day();

      if (this.activeSlot.edit) {
        this.slotUpdated.emit({
          slot: {
            weekDay: this.activeSlot.data.weekDay,
            startMinute: this.activeSlot.data.startMinute,
            endMinute: this.activeSlot.data.endMinute
          },
          updates: {
            weekDay,
            startMinute,
            endMinute
          }
        });
      } else {
        this.slotAdded.emit({ endMinute, startMinute, weekDay });
      }
    } else if (this.radioValue === 'Override') {
      const dateInOrgTimeZone = spacetime(this.activeSlot.date).goto(
        this.schedule?.timezone || ORG_TIMEZONE
      );
      const startInOrgTimeZone = spacetime(this.activeSlot.startTime).goto(
        this.schedule?.timezone || ORG_TIMEZONE
      );
      const endInOrgTimeZone = spacetime(this.activeSlot.endTime).goto(
        this.schedule?.timezone || ORG_TIMEZONE
      );
      const startMinute =
        startInOrgTimeZone.hour() * 60 + startInOrgTimeZone.minute();
      const endMinute =
        endInOrgTimeZone.hour() * 60 + endInOrgTimeZone.minute();
      const date = dateInOrgTimeZone.toNativeDate();
      if (this.activeSlot.edit) {
        this.overrideUpdated.emit({
          override: {
            date: this.activeSlot.data.date,
            startMinute: this.activeSlot.data.startMinute,
            endMinute: this.activeSlot.data.endMinute,
            isAvailable: !!this.activeSlot.data.isAvailable
          },
          updates: {
            date,
            startMinute,
            endMinute,
            isAvailable: this.activeSlot.isAvailable
          }
        });
      } else {
        const date = dateInOrgTimeZone.toNativeDate();
        date.setUTCHours(12); // to make sure current time won't make an issue with different timezone +/-12 hours
        const override: IScheduleOverride = {
          date,
          startMinute:
            startInOrgTimeZone.hour() * 60 + startInOrgTimeZone.minute(),
          endMinute: endInOrgTimeZone.hour() * 60 + endInOrgTimeZone.minute(),
          isAvailable: this.activeSlot.isAvailable
        };
        this.overrideAdded.emit(override);
      }
    }

    this.closeDialog();
  }
  checkAvailabilityOverlap() {
    return (this.availability || []).some(
      s =>
        isWithinInterval(this.activeSlot.startTime, {
          start: s.StartTime,
          end: s.EndTime
        }) ||
        isWithinInterval(this.activeSlot.endTime, {
          start: s.StartTime,
          end: s.EndTime
        })
    );
  }

  handleCancel(): void {
    this.closeDialog();
  }

  deleteActiveSlot() {
    const { endMinute, startMinute, weekDay } = this.activeSlot.data;
    this.slotDeleted.emit({
      endMinute,
      startMinute,
      weekDay
    });
    this.closeDialog();
  }

  deleteActiveOverride() {
    const { endMinute, startMinute, date, isAvailable } = this.activeSlot.data;
    this.overrideDeleted.emit({
      endMinute,
      startMinute,
      date,
      isAvailable
    });
    this.closeDialog();
  }

  closeDialog() {
    this.isVisible = false;
    this.showStartTimeAfterEndTimeError = false;
    this.showAvailabilityOverlapError = false;
    this.activeSlot = { startTime: null, endTime: null, data: null };
  }

  renderEventsFromSchedule() {
    this.availability = (this.schedule.slots || []).map(slot => {
      let now = new Date();
      const currentDay = now.getDay();
      const distance = slot.weekDay - currentDay;
      now = spacetime(
        now.toDateString(),
        this.schedule?.timezone || ORG_TIMEZONE
      ).toNativeDate();

      // now.setDate(now.getDate() + distance);
      const startDate = addDays(now, distance);
      const endDate = addDays(now, distance);
      startDate.setMinutes(slot.startMinute);
      endDate.setMinutes(slot.endMinute);

      return {
        Subject: 'Availability',
        StartTime: startDate,
        EndTime: endDate,
        RecurrenceRule: 'FREQ=WEEKLY;',
        ...slot
      };
    });

    this.overrides = (this.schedule.overrides || []).map(override => {
      let date = new Date(override.date);
      date = spacetime(
        date.toDateString(),
        this.schedule?.timezone || ORG_TIMEZONE
      ).toNativeDate();
      return {
        Subject: override.id ? 'Busy' : 'Override',
        StartTime: addMinutes(date, override.startMinute),
        EndTime: addMinutes(date, override.endMinute),
        Color: override.isAvailable
          ? 'green'
          : override.id
          ? 'palevioletred'
          : 'red',
        ...override
      };
    });

    this.eventSettings = {
      ...this.eventSettings,
      dataSource: [...this.availability, ...this.overrides]
    };
  }

  applyCategoryColor(args: EventRenderedArgs): void {
    let color: string = args.data.Color as string;
    if (!args.element || !color) {
      return;
    }
    if (this.scheduleObj.currentView === 'Agenda') {
      (args.element.firstChild as HTMLElement).style.borderLeftColor = color;
    } else {
      args.element.style.backgroundColor = color;
    }
  }
}
